import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, BadgeDollarSign, Menu, X, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/shared/NotificationBell";

const MENU = [
  { to: "/agen", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/agen/jamaah", label: "Jamaah Saya", icon: Users },
  { to: "/agen/komisi", label: "Komisi", icon: BadgeDollarSign },
];

function SidebarContent({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 p-4" data-testid="agent-sidebar-nav">
      {MENU.map((m) => (
        <NavLink key={m.to} to={m.to} end={m.exact} onClick={onNavigate} data-testid={`agent-sidebar-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#0F6B4F] text-white" : "text-[#1F2937] hover:bg-[#F5B700]/15"}`}>
          <m.icon className="h-4 w-4 shrink-0" /> {m.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AgentLayout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen bg-[#FFFBEB]" data-testid="agent-layout">
      <aside className="hidden w-64 shrink-0 border-r border-amber-100 bg-white lg:block">
        <div className="flex h-16 items-center gap-2 border-b border-amber-100 px-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#F5B700] to-[#0F6B4F] text-white font-heading font-bold">A</div>
          <div className="leading-tight">
            <span className="block font-heading text-sm font-bold text-[#1F2937]">Portal Agen</span>
            <span className="block text-xs text-[#92720C]">KBIHU Muhammadiyah Surabaya</span>
          </div>
        </div>
        <SidebarContent />
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex h-16 items-center justify-between gap-4 border-b border-amber-100 bg-white px-4 sm:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden" data-testid="agent-mobile-menu-toggle" aria-label="Menu"><Menu className="h-5 w-5 text-[#1F2937]" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-white p-0">
              <div className="flex h-16 items-center justify-between border-b border-amber-100 px-5">
                <span className="font-heading text-base font-bold text-[#1F2937]">Portal Agen</span>
                <button onClick={() => setMobileOpen(false)} data-testid="agent-mobile-menu-close"><X className="h-5 w-5" /></button>
              </div>
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <span className="font-heading text-sm font-semibold text-[#1F2937] lg:hidden">Portal Agen</span>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="flex items-center gap-2 border-l border-amber-100 pl-3">
              <p className="hidden text-sm font-medium text-[#1F2937] sm:block" data-testid="agent-user-name">{user?.name}</p>
              <Button variant="ghost" size="icon" data-testid="agent-logout-button" onClick={() => logout().then(() => navigate("/"))} className="text-[#64748B] hover:text-red-600">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden p-4 sm:p-6"><Outlet /></main>
      </div>
    </div>
  );
}
